import { BASE_URL_API } from '@constants/base';
import { get, post } from '@utils/ajax';
import dayjs from 'dayjs';
import itemFakeDataService from './item-fake-data-service';

/**
 * Item Service
 * Handles all item-related API calls
 */
const itemService = (() => {
    // Fetch items with search and filter parameters using new stockProduct API
    const fetchItems = async params => {
        try {
            const response = await get(`${BASE_URL_API}/main/stockProduct/list.php`, params, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Fetch item by ID
    const fetchItemById = async itemId => {
        try {
            const response = await get(
                `${BASE_URL_API}/main/stockProduct/edit_api.php`,
                {
                    method: 'info',
                    stock_product_id: itemId
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    // Update item
    const updateItem = async (itemId, itemData) => {
        try {
            // Update using stockProduct edit API
            const response = await post(
                `${BASE_URL_API}/main/stockProduct/edit_api.php`,
                {
                    method: 'update',
                    stock_product_id: itemId,
                    ...itemData
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    // Delete item using action_button_api.php
    const deleteItem = async (itemId, emailNotifications = {}) => {
        try {
            const deleteData = {
                method: 'delete',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, deleteData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Export items to CSV
    const exportItemsToCSV = async params => {
        try {
            const response = await get(`${BASE_URL_API}/main/item/export.php`, params, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Export items to Excel
    const exportItemsToExcel = async params => {
        try {
            // Build query params - convert all keys to snake_case
            const queryParams = new URLSearchParams();
            queryParams.append('method', 'search');

            Object.keys(params).forEach(key => {
                if (params[key] !== undefined && params[key] !== null) {
                    // Skip empty strings but keep false values (for checkboxes)
                    if (params[key] === '' && typeof params[key] !== 'boolean') {
                        return;
                    }

                    // Convert key to snake_case
                    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
                    // Convert boolean false to '0', true to '1', keep other values as is
                    const value = typeof params[key] === 'boolean' ? (params[key] ? '1' : '0') : params[key];
                    queryParams.append(snakeKey, value);
                }
            });

            const excelUrl = `/admin/excel/a_item_all_list_excel.php?${queryParams.toString()}`;

            // Use fetch to download file
            const response = await fetch(excelUrl, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Excel export failed');
            }

            // Get blob from response
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `在庫商品一覧_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { status: 'success', message: 'Excel file downloaded successfully' };
        } catch (error) {
            return { status: 'error', message: error.message || 'Excel export failed' };
        }
    };

    // Get item statistics
    const getItemStatistics = async () => {
        try {
            const response = await get(
                `${BASE_URL_API}/main/item/statistics.php`,
                {},
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    // Get status map for display
    const getStatusMap = () => {
        return itemFakeDataService.getStatusMap();
    };

    // Add new item
    const addItem = async itemData => {
        try {
            const response = await post(
                `${BASE_URL_API}/main/item/create.php`,
                {
                    method: 'create',
                    item_data: itemData
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    // Create stock product
    const createStockProduct = async stockData => {
        try {
            const response = await post('/admin/api/main/stockProduct/insert_api.php', stockData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Work request using action_button_api.php
    const workRequest = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'request_process',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Storage extension request using action_button_api.php
    const storageExtensionRequest = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'request_extend',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Process request using action_button_api.php with method = 'process'
    const processRequest = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'process',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Accept process using action_button_api.php
    const acceptProcess = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'accept_process',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Reject process using action_button_api.php
    const rejectProcess = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'reject_process',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Update process using action_button_api.php
    const updateProcess = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'update_process',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Accept extend using action_button_api.php
    const acceptExtend = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'accept_extend',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Reject extend using action_button_api.php
    const rejectExtend = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'reject_extend',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Upload item images
    const uploadItemImages = async (files, itemCode) => {
        try {
            const formData = new FormData();

            // Add images to FormData
            files.forEach(file => {
                formData.append('images[]', file);
            });

            // Add item code
            formData.append('item_cd', itemCode);

            const response = await fetch(`${BASE_URL_API}/main/itemManagement/edit_image_api.php`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Upload error:', error);
        }
    };

    // Delete images by updating image list (removes deleted images from server)
    const deleteImage = async (imageList, itemCode) => {
        try {
            const formData = new FormData();

            // Add image list as JSON (remaining images after deletion)
            formData.append('image_list', JSON.stringify(imageList));

            // Add item code
            formData.append('item_cd', itemCode);

            const response = await fetch(`${BASE_URL_API}/main/itemManagement/edit_image_api.php`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Delete image error:', error);
            return {
                status: 'error',
                message: '画像の削除中にエラーが発生しました。'
            };
        }
    };

    // Accept delete using action_button_api.php
    const acceptDelete = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'accept_delete',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Reject delete using action_button_api.php
    const rejectDelete = async (itemId, emailNotifications = {}) => {
        try {
            const requestData = {
                method: 'reject_delete',
                stock_product_id: itemId,
                mail_for_packaging_staff: emailNotifications.packStaff ? '1' : '',
                mail_for_shipping_staff: emailNotifications.shippingStaff ? '1' : '',
                mail_for_client_staff: emailNotifications.customerStaff ? '1' : ''
            };

            const response = await post(`${BASE_URL_API}/main/stockProduct/action_button_api.php`, requestData, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Get product info
    const getProductInfo = async itemCode => {
        try {
            const response = await get(`${BASE_URL_API}/main/stockProduct/product_info_api.php`, {
                item_cd: itemCode
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    return {
        fetchItems,
        fetchItemById,
        updateItem,
        deleteItem,
        addItem,
        exportItemsToCSV,
        exportItemsToExcel,
        getItemStatistics,
        getStatusMap,
        createStockProduct,
        workRequest,
        storageExtensionRequest,
        processRequest,
        acceptProcess,
        rejectProcess,
        updateProcess,
        acceptExtend,
        rejectExtend,
        acceptDelete,
        rejectDelete,
        uploadItemImages,
        deleteImage,
        getProductInfo
    };
})();

export default itemService;
